import {
  Button,
  Icon,
  Popover,
  PopoverContent,
  PopoverLauncher,
  Typography,
} from '@vp/swan';
import { ReplaceTokens, useTranslations } from '@vp/om-translations';
import { EmbeddableLinkCreator } from '../shared/EmbeddableLinkCreator';
import { useSiteData } from '../../hooks/useSiteData';
import { CancellationType } from '../../constants/cancellation';
import type { ICancellationType } from '../../types/cancellation.types';
import { getTranslationKeyFromCancellationType } from '../../utils/cancellation';

type ItemStatusTooltipProps = {
  detail: ICancellationType;
};

export const ItemStatusTooltip = ({ detail }: ItemStatusTooltipProps) => {
  const [cancellationReasons = {}] = useTranslations(['cancellationReasons']);

  const cancellationReasonText: string = cancellationReasons.cancellationReason;
  const { externalAppNavigation, embroideryUrl } = useSiteData();
  const navToTermsAndConditions = externalAppNavigation.termsAndConditions;

  const TermsAndConditionsLink = EmbeddableLinkCreator({
    href: navToTermsAndConditions,
  });

  const EmbroideryGuidelinesLink = EmbeddableLinkCreator({
    href: embroideryUrl,
  });

  // get translatedReason for CancellationType
  const translationKey: string = getTranslationKeyFromCancellationType(detail);
  const translatedReason: string = cancellationReasons[translationKey] || '';

  // verify feature is enabled and StateQuantity `detail` property matches one of the CancellationTypes (with translated reasons)
  const shouldShowCancellationTooltip: boolean =
    detail in CancellationType && translatedReason !== '';

  return shouldShowCancellationTooltip ? (
    <Popover ml={1}>
      <PopoverLauncher>
        {/* minimum click target size must be 24px x 24px: https://www.w3.org/WAI/WCAG22/Understanding/target-size-minimum.html */}
        <Button
          skin="unstyled"
          style={{ minWidth: 24, minHeight: 24 }}
          aria-label={cancellationReasonText}
        >
          <Icon iconType="info" skin="error" alt={cancellationReasonText} />
        </Button>
      </PopoverLauncher>
      <PopoverContent
        aria-label={`${cancellationReasonText}: ${translatedReason}`}
      >
        <Typography fontWeight="bold">{cancellationReasonText}: </Typography>
        <Typography>
          <ReplaceTokens
            tokenizedString={translatedReason}
            substitutions={{
              TermsAndConditionsLink,
              EmbroideryGuidelinesLink,
            }}
          />
        </Typography>
      </PopoverContent>
    </Popover>
  ) : null;
};
